import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  defaultModelLabUiPreferences,
  defaultSlateUiPreferences,
  type ModelLabUiPreferences,
  type SlateUiPreferences,
  type UiPreferencesPatch,
  type UiPreferencesV1,
} from "./api";
import { useAuth } from "./auth";
import { config } from "./config";

const LEGACY_SLATE_KEY = "pb_tweaks";

type LocalBundle = {
  slate: SlateUiPreferences;
  model_lab: ModelLabUiPreferences;
};

function cacheKey(leagueId: string): string {
  return `predabet.ui_prefs.${leagueId}.v1`;
}

function readLocalBundle(leagueId: string): LocalBundle | null {
  try {
    const raw = localStorage.getItem(cacheKey(leagueId));
    if (!raw) return null;
    const p = JSON.parse(raw) as unknown;
    if (typeof p !== "object" || p === null) return null;
    const rec = p as Record<string, unknown>;
    const slate = rec.slate as unknown;
    const model_lab = rec.model_lab as unknown;
    if (typeof slate !== "object" || slate === null || typeof model_lab !== "object" || model_lab === null) {
      return null;
    }
    return {
      slate: { ...defaultSlateUiPreferences(), ...(slate as Partial<SlateUiPreferences>) },
      model_lab: { ...defaultModelLabUiPreferences(), ...(model_lab as Partial<ModelLabUiPreferences>) },
    };
  } catch {
    return null;
  }
}

function writeLocalBundle(leagueId: string, bundle: LocalBundle): void {
  try {
    localStorage.setItem(cacheKey(leagueId), JSON.stringify(bundle));
  } catch {
    /* ignore */
  }
}

/** Migrate pre-v1 slate tweaks (camelCase) into slate prefs. */
function readLegacySlateTweaks(): Partial<SlateUiPreferences> | null {
  try {
    const raw = localStorage.getItem(LEGACY_SLATE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Record<string, unknown>;
    const out: Partial<SlateUiPreferences> = {};
    if (p.probStyle === "bar" || p.probStyle === "number" || p.probStyle === "gauge") {
      out.prob_style = p.probStyle;
    }
    if (typeof p.groupByDate === "boolean") out.group_by_date = p.groupByDate;
    if (typeof p.hideEmptyMarket === "boolean") out.hide_empty_market = p.hideEmptyMarket;
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

function initialBundle(leagueId: string): LocalBundle {
  const fromCache = readLocalBundle(leagueId);
  const legacy = readLegacySlateTweaks();
  const slate = {
    ...defaultSlateUiPreferences(),
    ...(legacy ?? {}),
    ...(fromCache?.slate ?? {}),
  };
  const model_lab = {
    ...defaultModelLabUiPreferences(),
    ...(fromCache?.model_lab ?? {}),
  };
  return { slate, model_lab };
}

type DisplayPreferencesContextValue = {
  slate: SlateUiPreferences;
  modelLab: ModelLabUiPreferences;
  updateSlate: (patch: Partial<SlateUiPreferences>) => void;
  updateModelLab: (patch: Partial<ModelLabUiPreferences>) => void;
  /** True after local hydration; remote fetch finished or skipped. */
  remoteHydrated: boolean;
};

const DisplayPreferencesContext = createContext<DisplayPreferencesContextValue | null>(null);

const SAVE_DEBOUNCE_MS = 450;

export function DisplayPreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const leagueId = config.leagueId;
  const [bundle, setBundle] = useState<LocalBundle>(() => initialBundle(leagueId));
  const [remoteHydrated, setRemoteHydrated] = useState(false);

  const pendingPut = useRef<UiPreferencesPatch>({});
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqSeq = useRef(0);

  const flushSave = useCallback(async () => {
    const token = user?.idToken;
    const patch = pendingPut.current;
    pendingPut.current = {};
    if (!token || (!patch.slate && !patch.model_lab)) return;

    const mySeq = ++reqSeq.current;
    try {
      const doc = await api.setUiPreferences(patch, { idToken: token });
      if (mySeq !== reqSeq.current) return;
      setBundle({ slate: doc.slate, model_lab: doc.model_lab });
      writeLocalBundle(leagueId, { slate: doc.slate, model_lab: doc.model_lab });
    } catch (err) {
      console.warn("Failed to save UI preferences", err as Error);
    }
  }, [user?.idToken, leagueId]);

  const scheduleSave = useCallback(
    (fragment: UiPreferencesPatch) => {
      if (fragment.slate) {
        pendingPut.current.slate = { ...pendingPut.current.slate, ...fragment.slate };
      }
      if (fragment.model_lab) {
        pendingPut.current.model_lab = { ...pendingPut.current.model_lab, ...fragment.model_lab };
      }
      if (!user?.idToken) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveTimer.current = null;
        void flushSave();
      }, SAVE_DEBOUNCE_MS);
    },
    [user?.idToken, flushSave],
  );

  useEffect(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    pendingPut.current = {};
    reqSeq.current += 1;
    if (!user) {
      setBundle(initialBundle(leagueId));
      setRemoteHydrated(true);
      return;
    }
    setRemoteHydrated(false);
    let cancelled = false;
    void api
      .getUiPreferences({ idToken: user.idToken })
      .then((doc: UiPreferencesV1) => {
        if (cancelled) return;
        const next = { slate: doc.slate, model_lab: doc.model_lab };
        setBundle(next);
        writeLocalBundle(leagueId, next);
      })
      .catch((err) => {
        console.warn("Failed to load UI preferences; using local cache", err as Error);
      })
      .finally(() => {
        if (!cancelled) setRemoteHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user, leagueId]);

  const updateSlate = useCallback(
    (patch: Partial<SlateUiPreferences>) => {
      setBundle((prev) => {
        const next = { ...prev, slate: { ...prev.slate, ...patch } };
        writeLocalBundle(leagueId, next);
        return next;
      });
      scheduleSave({ slate: patch });
    },
    [leagueId, scheduleSave],
  );

  const updateModelLab = useCallback(
    (patch: Partial<ModelLabUiPreferences>) => {
      setBundle((prev) => {
        const next = { ...prev, model_lab: { ...prev.model_lab, ...patch } };
        writeLocalBundle(leagueId, next);
        return next;
      });
      scheduleSave({ model_lab: patch });
    },
    [leagueId, scheduleSave],
  );

  const value = useMemo(
    () => ({
      slate: bundle.slate,
      modelLab: bundle.model_lab,
      updateSlate,
      updateModelLab,
      remoteHydrated,
    }),
    [bundle.slate, bundle.model_lab, updateSlate, updateModelLab, remoteHydrated],
  );

  return (
    <DisplayPreferencesContext.Provider value={value}>{children}</DisplayPreferencesContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDisplayPreferences(): DisplayPreferencesContextValue {
  const ctx = useContext(DisplayPreferencesContext);
  if (!ctx) {
    throw new Error("useDisplayPreferences must be used inside <DisplayPreferencesProvider>");
  }
  return ctx;
}
